import React from "react";
import { CheckCircle2, XCircle, HelpCircle, MinusCircle, ShieldCheck, AlertTriangle, Inbox } from "lucide-react";

const ELIG = {
  PASS: { label: "PASS", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  FAIL: { label: "FAIL", cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  UNKNOWN: { label: "UNKNOWN", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: HelpCircle },
  NOT_APPLICABLE: { label: "N/A", cls: "bg-slate-100 text-slate-500 border-slate-200", Icon: MinusCircle },
};

export function EligibilityBadge({ state, testid }) {
  const cfg = ELIG[state] || ELIG.UNKNOWN;
  const { Icon } = cfg;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cfg.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {cfg.label}
    </span>
  );
}

const STATUS = {
  CURRENT: "bg-emerald-50 text-emerald-700 border-emerald-200",
  HISTORICAL: "bg-slate-100 text-slate-600 border-slate-200",
  UNVERIFIED: "bg-amber-50 text-amber-700 border-amber-200",
  CONFLICTED: "bg-orange-50 text-orange-700 border-orange-200",
  RETIRED: "bg-red-50 text-red-700 border-red-200",
};

export function StatusBadge({ status, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS[status] || STATUS.UNVERIFIED}`}>
      {status}
    </span>
  );
}

export function SourceBadge({ scholarship }) {
  return (
    <div className="rounded-xl border border-purple-100 bg-[#FBFAFF] p-3 text-xs" data-testid="source-badge">
      <div className="flex items-center gap-1.5 font-semibold text-[#4C1D95]">
        <ShieldCheck className="h-4 w-4" /> Sumber & Verifikasi
      </div>
      <ul className="mt-1.5 space-y-0.5 text-slate-600">
        <li><span className="text-slate-400">Sumber:</span> {scholarship.source_name} ({scholarship.source_type})</li>
        <li><span className="text-slate-400">Siklus:</span> {scholarship.cycle}</li>
        <li><span className="text-slate-400">Terverifikasi:</span> {scholarship.verified_date || "Belum diverifikasi"}</li>
        <li><span className="text-slate-400">Confidence:</span> {scholarship.confidence}</li>
      </ul>
      <a href={scholarship.source_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block font-semibold text-[#7C3AED] hover:underline">
        Verifikasi di situs resmi →
      </a>
      <p className="mt-1 text-[11px] italic text-slate-400">{scholarship.evidence_note}</p>
    </div>
  );
}

export function MetricCard({ icon: Icon, label, value, sub, accent = "#7C3AED", testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-purple-100 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        {Icon && (
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${accent}1A`, color: accent }}>
            <Icon className="h-4 w-4" />
          </span>
        )}
        <span className="text-xs font-medium text-slate-500">{label}</span>
      </div>
      <div className="mt-2 font-display text-2xl font-extrabold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

export function EmptyState({ title, desc, icon: Icon = Inbox, action, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-purple-200 bg-[#FBFAFF] p-10 text-center">
      <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-100 text-[#7C3AED]">
        <Icon className="h-6 w-6" />
      </span>
      <h4 className="font-display text-base font-bold text-slate-800">{title}</h4>
      {desc && <p className="mt-1 max-w-sm text-sm text-slate-500">{desc}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Disclaimer({ children }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

export function SectionTitle({ title, desc, action }) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">{title}</h2>
        {desc && <p className="mt-1 text-sm text-slate-500">{desc}</p>}
      </div>
      {action}
    </div>
  );
}
